import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import { ICountry, ISupportedVehicles, IVehicleUsages, ICoverTypes, IEmployerTypes, IOccupationTypes, IEnum } from '../../resources/interfaces';
import SupportedVehicle from '../../models/SupportedVehicle';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import { IInsurer } from '../../types';

interface IInitialState {
    SELECTED_COUNTRY?: ICountry;
    COUNTRIES?: ICountry[];
    SUPPORTED_VEHICLES: ISupportedVehicles[];
    VEHICLE_USAGES: Record<string, IVehicleUsages>;
    COVER_TYPES: Record<string, ICoverTypes>;
    EMPLOYER_TYPES: Record<string, IEmployerTypes>;
    OCCUPATION_TYPES: Record<string, IOccupationTypes>;
    VEHICLE_MAKES: string[];
    INSURERS: IInsurer[];
    ENUMS: {
        coverTypes: IEnum[];
        vehicleUsages: IEnum[];
        idTypes: IEnum[];
        bodyTypes: IEnum[];
        vehicleBodyTypes: IEnum[];
        usageGroups: IEnum[];
        companyTypes: IEnum[];
        currencyCodes: IEnum[];
        accountTypes: IEnum[];
        accountProviders: IEnum[];
        genders: IEnum[];
        employmentTypes: IEnum[];
        policyTypes: IEnum[];
        installmentPeriods: IEnum[];
        paymentStatuses: IEnum[];
        fuelSaleStatuses: IEnum[];
        communityTypes: IEnum[];
        intermediaryTypes: IEnum[];
        accountVerificationTypes: IEnum[];
        transactionTypes: IEnum[];
        payoutTypes: IEnum[];
        portfolios: IEnum[];
        installmentTypes: IEnum[];
        insuranceStatuses: IEnum[];
        issuanceModes: IEnum[];
        userDocumentTypes: IEnum[];
        claimsVehicleImages: IEnum[];
        leadTags: IEnum[];
        vehiclePhotoSides: IEnum[];
        paymentProcessors: IEnum[];
        imageViewTypes: IEnum[];
        employers: IEnum[];
        rateTypes: IEnum[];
        productTypes: IEnum[];
        insurerStatuses: IEnum[];
        fileTypes: IEnum[];
        leadSources: IEnum[];
        leadStatuses: IEnum[];
        discountRelations: IEnum[];
        policyAutomatedSmsNotificationTasks: IEnum[];
        serviceProviders: IEnum[];
        resourceTypes: IEnum[];
        eicCoverTypes: IEnum[];
        userAccessRights: IEnum[];
        specialPackageTypes: IEnum[];
        userTypes: IEnum[];
        reviewStages: IEnum[];
        roleTypes: IEnum[];
        planTiers: IEnum[];
        billingCycles: IEnum[];
        claimStages: IEnum[];
        claimTypes: IEnum[];
    };
}


export const initialState: IInitialState = {
    SELECTED_COUNTRY: {
        id: undefined,
        code: '',
        currencyIso: '',
        currencySymbol: '',
        flag: '',
        iso: '',
        language: '',
        name: undefined
    },
    COUNTRIES: [],
    SUPPORTED_VEHICLES: [],
    VEHICLE_USAGES: {},
    COVER_TYPES: {},
    EMPLOYER_TYPES: {},
    OCCUPATION_TYPES: {},
    VEHICLE_MAKES: [],
    INSURERS: [],
    ENUMS: {
        coverTypes: [],
        vehicleUsages: [],
        idTypes: [],
        claimStages: [],
        claimTypes: [],
        usageGroups: [],
        companyTypes: [
            {
                key: "INTERNAL",
                title: "Internal Team",
                description: "Brolly's internal staff and team members"
            },
            {
                key: "AGENCY",
                title: "Agency Partner",
                description: "External insurance agencies working with Brolly"
            },
            {
                key: "BROKER",
                title: "Brokerage Firm",
                description: "Insurance brokers representing clients"
            },
            {
                key: "AFFILIATE",
                title: "Affiliate Partner",
                description: "Business affiliates with partnership agreements"
            },
            {
                key: "INSURER",
                title: "Insurance Company",
                description: "Insurance providers that underwrite policies"
            },
            {
                key: "REPAIR_CENTER",
                title: "Auto Repair Center",
                description: "Vehicle repair and service centers"
            },
            {
                key: "FUEL_PROVIDER",
                title: "Fuel Provider",
                description: "Fuel service stations and providers"
            },
            {
                key: "FINANCIER",
                title: "Financial Institution",
                description: "Financial institutions providing funding"
            },
            {
                key: "RIDE_SHARING",
                title: "Ride Sharing Company",
                description: "Ride sharing companies and platforms"
            },
            {
                key: "FLEET",
                title: "Vehicle Fleet",
                description: "Companies with vehicle fleets"
            },
            {
                key: "OTHER",
                title: "Other",
                description: "Other types of teams not categorized above"
            }
        ],
        currencyCodes: [],
        accountTypes: [],
        accountProviders: [],
        genders:
            [{
                key: "MALE",
                title: "Male",
                description: "Person who identifies as male"
            },
            {
                key: "FEMALE",
                title: "Female",
                description: "Person who identifies as female"
            },
            {
                key: "UNKNOWN",
                title: "Unknown",
                description: "Gender not specified or unknown"
            }
            ],
        employmentTypes: [],
        policyTypes: [],
        installmentPeriods: [],
        paymentStatuses: [],
        fuelSaleStatuses: [],
        communityTypes: [],
        intermediaryTypes: [],
        accountVerificationTypes: [],
        transactionTypes: [],
        payoutTypes: [],
        installmentTypes: [],
        insuranceStatuses: [],
        issuanceModes: [],
        userDocumentTypes: [],
        claimsVehicleImages: [],
        leadTags: [],
        vehiclePhotoSides: [],
        paymentProcessors: [],
        imageViewTypes: [],
        employers: [],
        rateTypes: [],
        productTypes: [],
        insurerStatuses: [],
        fileTypes: [],
        leadSources: [],
        leadStatuses: [],
        discountRelations: [],
        policyAutomatedSmsNotificationTasks: [],
        serviceProviders: [],
        resourceTypes: [],
        eicCoverTypes: [],
        userAccessRights: [],
        specialPackageTypes: [],
        userTypes: [],
        roleTypes: [],
        reviewStages: [],
        bodyTypes: [],
        vehicleBodyTypes: [],
        planTiers: [],
        billingCycles: [],
        portfolios: [],
    }

};

const resource = createSlice({
    name: 'resource',
    initialState,
    reducers: {
        INIT_RESOURCES: (state, action: PayloadAction<typeof initialState>) => {
            state.SUPPORTED_VEHICLES = action.payload.SUPPORTED_VEHICLES;
            state.VEHICLE_USAGES = action.payload.VEHICLE_USAGES;
            state.COVER_TYPES = action.payload.COVER_TYPES;
            state.EMPLOYER_TYPES = action.payload.EMPLOYER_TYPES;
            state.OCCUPATION_TYPES = action.payload.OCCUPATION_TYPES;
            state.VEHICLE_MAKES = action.payload.VEHICLE_MAKES;
            state.INSURERS = action.payload.INSURERS;
            return state;
        },
        UPDATE_SUPPORTED_VEHICLES: (state, action: PayloadAction<ISupportedVehicles[]>) => {
            state.SUPPORTED_VEHICLES = [...action.payload];
            return state;
        },
        UPDATE_INSURERS: (state, action: PayloadAction<IInsurer[]>) => {
            state.INSURERS = [...action.payload];
            return state;
        },
        UPDATE_COUNTRIES_IN_STATE: (state, action: PayloadAction<ICountry[]>) => {
            state.COUNTRIES = action.payload;
        },
        ADD_COUNTRY: (state, action: PayloadAction<ICountry>) => {
            state.COUNTRIES?.push(action.payload);
        },
        SET_SELECTED_COUNTRY: (state, action: PayloadAction<ICountry | undefined>) => {
            state.SELECTED_COUNTRY = action.payload;
        },
        UPDATE_ENUMS: (state, action: PayloadAction<{
            coverTypes?: IEnum[];
            vehicleUsages?: IEnum[];
            idTypes?: IEnum[];
            usageGroups?: IEnum[];
            companyTypes?: IEnum[];
            currencyCodes?: IEnum[];
            accountTypes?: IEnum[];
            accountProviders?: IEnum[];
            genders?: IEnum[];
            employmentTypes?: IEnum[];
            policyTypes?: IEnum[];
            installmentPeriods?: IEnum[];
            paymentStatuses?: IEnum[];
            policyDocumentTypes?: IEnum[];
            fuelSaleStatuses?: IEnum[];
            communityTypes?: IEnum[];
            intermediaryTypes?: IEnum[];
            accountVerificationTypes?: IEnum[];
            transactionTypes?: IEnum[];
            payoutTypes?: IEnum[];
            installmentTypes?: IEnum[];
            insuranceStatuses?: IEnum[];
            userDocumentTypes?: IEnum[];
            vehicleDocumentTypes?: IEnum[];
            leadTags?: IEnum[];
            vehiclePhotoSides?: IEnum[];
            paymentProcessors?: IEnum[];
            imageViewTypes?: IEnum[];
            employers?: IEnum[];
            rateTypes?: IEnum[];
            productTypes?: IEnum[];
            issuanceModes?: IEnum[];
            insurerStatuses?: IEnum[];
            fileTypes?: IEnum[];
            leadSources?: IEnum[];
            leadStatuses?: IEnum[];
            discountRelations?: IEnum[];
            policyAutomatedSmsNotificationTasks?: IEnum[];
            serviceProviders?: IEnum[];
            resourceTypes?: IEnum[];
            eicCoverTypes?: IEnum[];
            userAccessRights?: IEnum[];
            specialPackageTypes?: IEnum[];
            userTypes?: IEnum[];
            roleTypes?: IEnum[];
            reviewStages?: IEnum[];
            claimStatuses?: IEnum[];
            claimTypes?: IEnum[];
            claimStages?: IEnum[];
            documentTypes?: IEnum[];
            bodyTypes?: IEnum[];
            vehicleMakes?: IEnum[];
            vehicleModels?: IEnum[];
            vehicleBodyTypes?: IEnum[];
            vehicleColors?: IEnum[];
            vehicleFuelTypes?: IEnum[];
            vehicleTransmissionTypes?: IEnum[];
            vehicleConditions?: IEnum[];
            vehicleOwnershipTypes?: IEnum[];
            vehicleUsagePurposes?: IEnum[];
            vehicleSecurityFeatures?: IEnum[];
            vehicleTrackingTypes?: IEnum[];
            vehicleAlarmTypes?: IEnum[];
            vehicleImmobilizerTypes?: IEnum[];
            vehicleParkingTypes?: IEnum[];
            vehicleGarageTypes?: IEnum[];
            planTiers?: IEnum[];
            billingCycles?: IEnum[];
            portfolios?: IEnum[];

        }>) => {
            // Initialize ENUMS if it doesn't exist
            state.ENUMS = state.ENUMS || { ...initialState.ENUMS };

            // Safely update each enum array
            const updateEnum = (key: keyof typeof initialState.ENUMS, value: IEnum[] | undefined) => {
                if (Array.isArray(value)) {
                    state.ENUMS[key] = value;
                }
            };

            // Update only the provided enum arrays
            Object.entries(action.payload).forEach(([key, value]) => {
                if (key in state.ENUMS) {
                    updateEnum(key as keyof typeof initialState.ENUMS, value as IEnum[]);
                }
            });
        }
    }
});

export const {
    SET_SELECTED_COUNTRY,
    UPDATE_COUNTRIES_IN_STATE,
    ADD_COUNTRY,
    UPDATE_SUPPORTED_VEHICLES,
    UPDATE_INSURERS,
    INIT_RESOURCES,
    UPDATE_ENUMS
} = resource.actions;

export const getCountriesFromState = (state: RootState): ICountry[] => state.resource.COUNTRIES;
export const getInsurersFromState = (state: RootState): IInsurer[] => state.resource.INSURERS;
export const getSelectedCountry = (state: RootState): ICountry => state.resource.SELECTED_COUNTRY;
export const getGroupedSupportedVehicles = (state: RootState): Record<string, Array<SupportedVehicle>> => {
    let groupedList: Record<string, Array<SupportedVehicle>> = {};
    const list = state.resource.SUPPORTED_VEHICLES?.map((item: ISupportedVehicles) => new SupportedVehicle(item));
    const sortedList = sortBy(list, "make");
    groupedList = groupBy(sortedList, "make");

    return groupedList;
}
export const getVehicleMakes = (state: RootState): Array<string> => {
    return state.resource.VEHICLE_MAKES?.filter((vehicleMake: string) => vehicleMake !== "Other Vehicles") || [];
}

export const getSupportedVehicles = (state: RootState): Array<ISupportedVehicles> => {
    return state.resource.SUPPORTED_VEHICLES?.filter((supportedVehicle: ISupportedVehicles) => supportedVehicle?.maker?.toLowerCase() !== "other vehicles") || [];
}

export const getEnumsFromState = (state: RootState) => state.resource.ENUMS;

export default resource.reducer;
